// src/context/WalletContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

export interface WalletState {
  connected: boolean;
  address: string | null;
  balance: string;
  balanceMEE: string;
  isDemo: boolean;
  chainId: string | null;
  badges: unknown[];
  badgesLoading: boolean;
}

interface WalletContextValue extends WalletState {
  connect: (type?: "metamask" | "demo") => Promise<void>;
  disconnect: () => void;
  refreshBalance: () => void;
  openSend: () => void;
  openReceive: () => void;
  openWalletModal: () => void;
}

const defaultState: WalletState = {
  connected: false,
  address: null,
  balance: "0",
  balanceMEE: "0",
  isDemo: false,
  chainId: null,
  badges: [],
  badgesLoading: false,
};

const WalletContext = createContext<WalletContextValue>({
  ...defaultState,
  connect: async () => {},
  disconnect: () => {},
  refreshBalance: () => {},
  openSend: () => {},
  openReceive: () => {},
  openWalletModal: () => {},
});

function getWalletState(): WalletState {
  const ws = (window as any).WalletState;
  return ws ? { ...defaultState, ...ws } : { ...defaultState };
}

function getAPI() {
  return (window as any).WalletAPI;
}

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<WalletState>(defaultState);

  const sync = useCallback(() => setState(getWalletState()), []);

  useEffect(() => {
    window.addEventListener("walletConnected", sync);
    window.addEventListener("walletDisconnected", sync);
    return () => {
      window.removeEventListener("walletConnected", sync);
      window.removeEventListener("walletDisconnected", sync);
    };
  }, [sync]);

  const connect = useCallback(async (type: "metamask" | "demo" = "metamask") => {
    const api = getAPI();
    if (api?.connect) await api.connect(type);
    sync();
  }, [sync]);

  const disconnect = useCallback(() => {
    getAPI()?.disconnect?.();
    sync();
  }, [sync]);

  const refreshBalance = useCallback(() => { getAPI()?.refreshBalance?.(); }, []);
  const openSend       = useCallback(() => { getAPI()?.openSend?.();       }, []);
  const openReceive    = useCallback(() => { getAPI()?.openReceive?.();    }, []);
  const openWalletModal= useCallback(() => {
    document.getElementById("wallet-modal")?.classList.remove("hidden");
  }, []);

  return (
    <WalletContext.Provider value={{ ...state, connect, disconnect, refreshBalance, openSend, openReceive, openWalletModal }}>
      {children}
    </WalletContext.Provider>
  );
}

export const useWallet = () => useContext(WalletContext);